import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
    Boxes,
    Plus,
    Sparkles,
    Play,
    Trash2,
    Archive,
    Upload,
    X,
} from "lucide-react";

import SidebarLayout from "@/components/SidebarLayout";
import { api } from "@/lib/api";

const STATUS_COLOR = {
    active: "#CCCCCC",
    draft: "#A3A3A3",
    archived: "#CCCCCC",
};

export default function AdminModules() {
    const [items, setItems] = useState([]);
    const [status, setStatus] = useState("");
    const [loading, setLoading] = useState(true);
    const [showBuilder, setShowBuilder] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await api.adminModulesList({ status: status || undefined });
            setItems(d.items || []);
        } catch (e) {
            toast.error(e.message || "Failed to load modules");
        } finally {
            setLoading(false);
        }
    }, [status]);

    useEffect(() => {
        load();
    }, [load]);

    async function publish(id) {
        try {
            await api.adminModulePublish(id);
            toast.success("Published");
            await load();
        } catch (e) {
            toast.error(e.message || "Publish failed");
        }
    }
    async function archive(id) {
        try {
            await api.adminModuleArchive(id);
            toast.success("Archived");
            await load();
        } catch (e) {
            toast.error(e.message);
        }
    }
    async function remove(id) {
        if (!window.confirm("Delete this module and cascade its runs?")) return;
        try {
            await api.adminModuleDelete(id);
            toast.success("Deleted");
            await load();
        } catch (e) {
            toast.error(e.message);
        }
    }

    return (
        <SidebarLayout>
            <div className="mx-auto max-w-7xl">
                <motion.header
                    initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                    className="mb-6 flex items-end justify-between flex-wrap gap-4"
                >
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-text-secondary">
                            Admin · Module Builder
                        </p>
                        <h1 className="text-3xl md:text-4xl font-semibold mt-1">Custom Modules</h1>
                        <p className="text-text-secondary mt-2 text-sm max-w-xl">
                            Schema-driven, safe AI modules published to your users. No code eval — pure prompt templates + OpenAI whitelist.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <select
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            className="rounded-full border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-1.5 text-sm outline-none"
                        >
                            <option value="">All</option>
                            <option value="active">Active</option>
                            <option value="draft">Draft</option>
                            <option value="archived">Archived</option>
                        </select>
                        <button
                            data-testid="admin-modules-ai-suggest"
                            onClick={() => setShowSuggestions(true)}
                            className="inline-flex items-center gap-2 rounded-full border border-[var(--color-primary)]/40 bg-[var(--color-primary)]/10 text-[var(--color-primary)] px-4 py-2 text-sm"
                        >
                            <Sparkles className="h-4 w-4" /> AI Suggest
                        </button>
                        <button
                            data-testid="admin-modules-new"
                            onClick={() => setShowBuilder(true)}
                            className="inline-flex items-center gap-2 rounded-full bg-white hover:bg-[#EDEDED] text-black px-4 py-2 text-sm font-medium"
                        >
                            <Plus className="h-4 w-4" /> New Module
                        </button>
                    </div>
                </motion.header>

                {loading ? (
                    <div className="text-text-secondary text-sm">Loading…</div>
                ) : (
                    <div data-testid="admin-modules-grid" className="grid gap-4 md:grid-cols-2">
                        {items.map((m) => (
                            <div
                                key={m.id}
                                className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5"
                                data-testid={`admin-module-${m.slug}`}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="text-[10px] uppercase tracking-widest" style={{ color: STATUS_COLOR[m.status] }}>
                                            {m.status}{m.ai_suggested ? " · AI-suggested" : ""}
                                        </p>
                                        <h3 className="font-medium mt-0.5 truncate">{m.name}</h3>
                                        <p className="text-xs text-text-secondary mt-0.5">
                                            slug: {m.slug} · plan: {m.required_min_plan} · cost: {m.cost_credits}
                                        </p>
                                    </div>
                                    <div className="text-xs text-text-secondary text-right">
                                        <p>{m.stats?.total_runs || 0} runs</p>
                                        <p className="mt-0.5">
                                            {(m.stats?.credits_generated_lifetime || 0)} cr generated
                                        </p>
                                    </div>
                                </div>
                                <p className="text-sm text-text-secondary mt-3 line-clamp-2">{m.description}</p>
                                <div className="mt-4 flex flex-wrap items-center gap-2">
                                    {m.status !== "active" && (
                                        <button
                                            onClick={() => publish(m.id)}
                                            data-testid={`admin-module-publish-${m.slug}`}
                                            className="inline-flex items-center gap-1 rounded-full bg-[var(--color-success)]/15 border border-[var(--color-success)]/40 text-[var(--color-success)] px-3 py-1 text-xs"
                                        >
                                            <Upload className="h-3 w-3" /> Publish
                                        </button>
                                    )}
                                    {m.status === "active" && (
                                        <button
                                            onClick={() => archive(m.id)}
                                            data-testid={`admin-module-archive-${m.slug}`}
                                            className="inline-flex items-center gap-1 rounded-full border border-[var(--color-border)] text-text-secondary px-3 py-1 text-xs"
                                        >
                                            <Archive className="h-3 w-3" /> Archive
                                        </button>
                                    )}
                                    <button
                                        onClick={() => remove(m.id)}
                                        data-testid={`admin-module-delete-${m.slug}`}
                                        className="inline-flex items-center gap-1 rounded-full border border-[var(--color-alert)]/40 text-[var(--color-alert)] px-3 py-1 text-xs"
                                    >
                                        <Trash2 className="h-3 w-3" /> Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {showBuilder && <ModuleBuilderModal onClose={() => setShowBuilder(false)} onSaved={load} />}
            {showSuggestions && <SuggestionsModal onClose={() => setShowSuggestions(false)} onSaved={load} />}
        </SidebarLayout>
    );
}

function emptySchema() {
    return {
        slug: "",
        name: "",
        description: "",
        icon: "sparkles",
        category: "custom",
        cost_credits: 5,
        required_min_plan: "free",
        input_schema: [
            { name: "input_text", label: "Input", type: "textarea", required: true, placeholder: "" },
        ],
        ai_config: {
            model: "gpt-4o-mini",
            system_prompt: "You are a helpful assistant.",
            user_prompt_template: "{{input_text}}",
            temperature: 0.7,
            max_tokens: 1500,
            output_format: "text",
        },
        output_render: { type: "text" },
    };
}

function ModuleBuilderModal({ onClose, onSaved, prefill }) {
    const [schema, setSchema] = useState(prefill || emptySchema());
    const [saving, setSaving] = useState(false);
    const [testInputs, setTestInputs] = useState({});
    const [testResult, setTestResult] = useState(null);
    const [testing, setTesting] = useState(false);

    function set(path, val) {
        setSchema((s) => {
            const copy = { ...s };
            const keys = path.split(".");
            let cur = copy;
            for (let i = 0; i < keys.length - 1; i++) {
                cur[keys[i]] = { ...cur[keys[i]] };
                cur = cur[keys[i]];
            }
            cur[keys[keys.length - 1]] = val;
            return copy;
        });
    }

    function addField() {
        set("input_schema", [...schema.input_schema, {
            name: `field_${schema.input_schema.length + 1}`, label: "New field",
            type: "text", required: false,
        }]);
    }
    function removeField(i) {
        set("input_schema", schema.input_schema.filter((_, j) => j !== i));
    }

    async function save() {
        setSaving(true);
        try {
            const created = await api.adminModuleCreate(schema);
            toast.success(`Module '${schema.slug}' created as draft`);
            onSaved();
            onClose();
        } catch (e) {
            toast.error(e.message || "Save failed");
        } finally {
            setSaving(false);
        }
    }

    async function testNow() {
        // Need to save first to get an id — quick create + test
        setTesting(true);
        try {
            const created = await api.adminModuleCreate(schema);
            const r = await api.adminModuleTest(created.module.id, testInputs);
            setTestResult(r);
            toast.success("Test executed (no credits consumed)");
            onSaved();
        } catch (e) {
            toast.error(e.message || "Test failed");
        } finally {
            setTesting(false);
        }
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto">
            <div className="w-full max-w-3xl rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] my-8">
                <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--color-border)]">
                    <h2 className="text-lg font-semibold">New Module</h2>
                    <button onClick={onClose} className="text-text-secondary hover:text-text-main">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <div className="p-6 space-y-6">
                    <div className="grid grid-cols-2 gap-3">
                        <Input label="Slug (unique)" value={schema.slug} onChange={(v) => set("slug", v)} placeholder="my-module-slug" />
                        <Input label="Name" value={schema.name} onChange={(v) => set("name", v)} />
                        <Input label="Icon (lucide slug)" value={schema.icon} onChange={(v) => set("icon", v)} />
                        <Select label="Category" value={schema.category} onChange={(v) => set("category", v)}
                            options={[
                                { value: "leads", label: "Leads" },
                                { value: "outreach", label: "Outreach" },
                                { value: "content", label: "Content" },
                                { value: "analysis", label: "Analysis" },
                                { value: "automation", label: "Automation" },
                                { value: "custom", label: "Custom" },
                            ]} />
                        <Input label="Cost credits" type="number" value={schema.cost_credits} onChange={(v) => set("cost_credits", parseInt(v || "1"))} />
                        <Select label="Required min plan" value={schema.required_min_plan} onChange={(v) => set("required_min_plan", v)}
                            options={[
                                { value: "free", label: "Free" },
                                { value: "starter", label: "Starter" },
                                { value: "pro", label: "Pro" },
                                { value: "business", label: "Business" },
                                { value: "enterprise", label: "Enterprise" },
                            ]} />
                    </div>
                    <div>
                        <label className="text-xs text-text-secondary block mb-1">Description</label>
                        <textarea rows={2} value={schema.description} onChange={(e) => set("description", e.target.value)}
                            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-ai)] px-3 py-2 text-sm outline-none" />
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-xs uppercase tracking-widest text-text-secondary">Input Schema</p>
                            <button onClick={addField} className="text-xs text-[var(--color-secondary)]">+ Add field</button>
                        </div>
                        <div className="space-y-2">
                            {schema.input_schema.map((f, i) => (
                                <div key={i} className="grid grid-cols-6 gap-2 items-end">
                                    <Input label="name" value={f.name} onChange={(v) => set(`input_schema.${i}.name`, v)} />
                                    <Input label="label" value={f.label} onChange={(v) => set(`input_schema.${i}.label`, v)} />
                                    <Select label="type" value={f.type} onChange={(v) => set(`input_schema.${i}.type`, v)}
                                        options={[
                                            { value: "text", label: "text" },
                                            { value: "textarea", label: "textarea" },
                                            { value: "select", label: "select" },
                                            { value: "multi_select", label: "multi_select" },
                                            { value: "number", label: "number" },
                                            { value: "tags", label: "tags" },
                                        ]} />
                                    <div className="col-span-2">
                                        <label className="text-xs text-text-secondary block">placeholder</label>
                                        <input value={f.placeholder || ""} onChange={(e) => set(`input_schema.${i}.placeholder`, e.target.value)}
                                            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-ai)] px-2 py-1 text-sm outline-none" />
                                    </div>
                                    <button onClick={() => removeField(i)} className="text-[var(--color-alert)]"><Trash2 className="h-4 w-4" /></button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div>
                        <p className="text-xs uppercase tracking-widest text-text-secondary mb-2">AI Config</p>
                        <div className="grid grid-cols-2 gap-3">
                            <Select label="Model" value={schema.ai_config.model} onChange={(v) => set("ai_config.model", v)}
                                options={[
                                    { value: "gpt-4o-mini", label: "gpt-4o-mini" },
                                    { value: "gpt-4o", label: "gpt-4o" },
                                ]} />
                            <Select label="Output format" value={schema.ai_config.output_format} onChange={(v) => set("ai_config.output_format", v)}
                                options={[
                                    { value: "text", label: "text" },
                                    { value: "markdown", label: "markdown" },
                                    { value: "json", label: "json" },
                                    { value: "list", label: "list" },
                                ]} />
                            <Input label="Temperature (0-1)" type="number" value={schema.ai_config.temperature} onChange={(v) => set("ai_config.temperature", parseFloat(v))} />
                            <Input label="Max tokens" type="number" value={schema.ai_config.max_tokens} onChange={(v) => set("ai_config.max_tokens", parseInt(v))} />
                        </div>
                        <label className="text-xs text-text-secondary block mt-3">System prompt (uses {`{{field}}`} tokens)</label>
                        <textarea rows={3} value={schema.ai_config.system_prompt} onChange={(e) => set("ai_config.system_prompt", e.target.value)}
                            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-ai)] px-3 py-2 text-sm outline-none" />
                        <label className="text-xs text-text-secondary block mt-3">User prompt template</label>
                        <textarea rows={4} value={schema.ai_config.user_prompt_template} onChange={(e) => set("ai_config.user_prompt_template", e.target.value)}
                            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-ai)] px-3 py-2 text-sm outline-none" />
                    </div>

                    {testResult && (
                        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-ai)] p-4">
                            <p className="text-xs uppercase tracking-widest text-text-secondary mb-2">Test result</p>
                            <pre className="text-xs whitespace-pre-wrap">{JSON.stringify(testResult.output, null, 2)}</pre>
                        </div>
                    )}

                    <div className="flex items-center gap-2 pt-3 border-t border-[var(--color-border)]">
                        <button
                            data-testid="admin-module-save"
                            onClick={save} disabled={saving}
                            className="rounded-full bg-white hover:bg-[#EDEDED] text-black px-5 py-2 text-sm font-medium disabled:opacity-50"
                        >
                            {saving ? "Saving…" : "Save Draft"}
                        </button>
                        <button
                            onClick={() => onClose()}
                            className="rounded-full border border-[var(--color-border)] px-5 py-2 text-sm"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function SuggestionsModal({ onClose, onSaved }) {
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [usedFallback, setUsedFallback] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await api.adminModuleSuggest();
            setSuggestions(d.suggestions || []);
            setUsedFallback(!!d.used_fallback);
            if (d.used_fallback) {
                toast.warning("AI suggest temporarily unavailable, showing curated presets");
            }
        } catch (e) {
            toast.error(e.message || "Suggest failed");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    async function apply(s) {
        try {
            await api.adminModuleSuggestApply(s);
            toast.success(`Draft '${s.slug}' created`);
            onSaved();
        } catch (e) {
            toast.error(e.message || "Apply failed");
        }
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto">
            <div
                data-testid="admin-suggest-modal"
                role="dialog"
                aria-label="AI-Suggested Modules"
                className="w-full max-w-3xl rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] my-8"
            >
                <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--color-border)]">
                    <div>
                        <h2 className="text-lg font-semibold flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-[var(--color-primary)]" />
                            AI-Suggested Modules
                        </h2>
                        {usedFallback && (
                            <p className="text-xs text-text-secondary">
                                Curated presets (LLM temporarily unavailable)
                            </p>
                        )}
                    </div>
                    <button onClick={onClose} className="text-text-secondary hover:text-text-main">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <div className="p-6 space-y-3">
                    {loading ? (
                        <div className="text-text-secondary text-sm">Generating suggestions…</div>
                    ) : (
                        suggestions.map((s, i) => (
                            <div
                                key={i}
                                data-testid={`admin-suggestion-${s.slug || i}`}
                                className="rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-ai)] p-4"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <h3 className="font-medium">{s.name}</h3>
                                        <p className="text-xs text-text-secondary mt-1">
                                            {s.category} · {s.cost_credits} cr · plan {s.required_min_plan}
                                        </p>
                                        <p className="text-sm text-text-secondary mt-2">{s.description}</p>
                                    </div>
                                    <button
                                        onClick={() => apply(s)}
                                        data-testid={`admin-suggestion-apply-${s.slug || i}`}
                                        className="rounded-full bg-[var(--color-primary)]/20 text-[var(--color-primary)] px-3 py-1 text-xs"
                                    >
                                        Save as Draft
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}

function Input({ label, value, onChange, type = "text", placeholder }) {
    return (
        <div>
            <label className="text-xs text-text-secondary block mb-1">{label}</label>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-ai)] px-3 py-2 text-sm outline-none focus:border-[var(--color-primary)]"
            />
        </div>
    );
}

function Select({ label, value, onChange, options }) {
    return (
        <div>
            <label className="text-xs text-text-secondary block mb-1">{label}</label>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-ai)] px-3 py-2 text-sm outline-none"
            >
                {options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                ))}
            </select>
        </div>
    );
}
